import React, {
  useCallback,
  useRef,
  useState,
  cloneElement,
  forwardRef,
  ReactElement,
  FocusEvent,
  FocusEventHandler
} from 'react';
import uniqueId from 'lodash.uniqueid';
import { useTheme, ThemeableElement } from '@design-systems/theme';
import InlineValidationMessage, {
  getAriaAttributes
} from '@ids-ts/inline-validation-message';
import clsx from 'clsx';
import { B2, B3, Demi } from '@ids-ts/typography';
import { IDS_PREFIX_FORM_CLASS, checkForA11yProps } from '@ids-ts/utils';
import logger from '@design-systems/logger';
import styles from './TextField.css';

export interface TextFieldProps extends ThemeableElement<'input'> {
  /** Prop to set desired Icon/ addonAfter on textfield. */
  addonAfter?: ReactElement;
  /** Prop to set desired Icon/ addonBefore on textfield. */
  addonBefore?: ReactElement;
  /** The aria-label property surfaces a hidden label to assistive technology. This is critical when the label property is not used. */
  'aria-label'?: string;
  /** DEPRECATED: Get reference to input field. */
  assignInputRef?: (c: HTMLInputElement | null) => void;
  /** Get reference to the label element */
  assignLabelRef?: (c?: HTMLElement | null) => void;
  /** AutomationID prop to be used for testing. */
  automationId?: string;
  /**
   * Disabled prop to pass on to root when set as true.
   *
   * @default false
   */
  disabled?: boolean;
  /** Error message to be rendered. */
  errorText?: string;
  /** Boolean flag to allow label to be bold */
  hasWeightedLabel?: boolean;
  /** Class to be applied to all the footer text (validation & helper). */
  footerTextClass?: string;
  /** A ghost text like placeholder, but can be controllable. */
  ghostText?: string;
  /** Text to be display for extra information */
  helperText?: string;
  /** Hide Arrows in the number type input element */
  hideArrows?: boolean;
  /**
   * Prop to set desired Icon on textfield. This Icon is from Icons-v3 component.
   * Deprecated. Please use addOnBefore or addOnAfter
   */
  icon?: ReactElement;
  /**
   * Position of the icon
   * Deprecated. Please use addOnBefore or addOnAfter
   *
   * @default 'right'
   */
  iconPosition?: 'right' | 'left';
  /** Id for component. */
  id?: string;
  /**
   * Label to be displayed for the field.
   *
   * @default ''
   */
  label?: string;
  /** Name to be assigned to the input field. */
  name?: string;
  /** Prop for handling callBack for onBlur event. */
  onBlur?: FocusEventHandler;
  /** Prop for handling callBack for onFocus event. */
  onFocus?: FocusEventHandler;
  /** placeholder label displayed in the field. */
  placeholder?: string;
  /**
   * Set input type (text, tel, number etc)
   *
   * @default 'text'
   */
  type?: string;
  /**
   * renders warning message.
   *
   * @default false
   */
  validationError?: boolean;
  /** Value to be displayed in the input field. */
  value?: number | string;
  /** warning message to be rendered. */
  warningText?: string;
  /**
   * Read-only prop to pass on to root when set as true.
   *
   * @default false
   */
  readOnly?: boolean;
  /**
   * Prevents CSS selector to be added for read-only styling.
   *
   * @default false
   */
  disableReadOnlyStyle?: boolean;
}

const prefix = 'idsTxtField';

/** Empty function to slightly improve render performance */
const noop = () => {};

/**
 * Text Field component.
 * Any supplied props that are not defined here will be provided to the native input element.
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  // eslint-disable-next-line complexity
  (props: TextFieldProps, ref) => {
    const {
      addonAfter: addonAfterProp,
      addonBefore: addonBeforeProp,
      hasWeightedLabel,
      assignInputRef,
      assignLabelRef,
      automationId,
      className,
      colorScheme,
      disabled = false,
      errorText,
      footerTextClass,
      ghostText,
      helperText,
      hideArrows,
      icon,
      iconPosition = 'right',
      id,
      label = '',
      onBlur = noop,
      onFocus = noop,
      style,
      theme,
      type = 'text',
      validationError = false,
      warningText,
      width,
      readOnly = false,
      disableReadOnlyStyle = false,
      ...html
    } = props;

    const [hasFocus, setHasFocus] = useState(false);
    const { current: ariaId } = useRef(uniqueId(prefix));
    const inputRef = useRef<HTMLInputElement | null>(null);
    const labelRef = useRef<HTMLElement | null>(null);
    const componentClassName = 'idsTSTextField';

    /** Function to get the reference of the input */
    const getInputRef = (c: HTMLInputElement | null) => {
      if (ref) return ref;

      inputRef.current = c;
      assignInputRef && assignInputRef(c);
    };

    /** Function to get the reference of the label */
    const getLabelRef = (c: HTMLElement | null) => {
      labelRef.current = c;
      assignLabelRef && assignLabelRef(c);
    };

    /** Sub-render method to render Icon/ Addon on textfield. */
    const renderAddOn = (addOn: ReactElement, isAddonAfter = false) => {
      const iconProps = icon
        ? {
            height: '20px',
            width: '20px',
            margin: '8px'
          }
        : null;
      const addonProps =
        // @ts-ignore: displayName exists on addOn.type
        addOn.type.displayName === 'Addon' && isAddonAfter
          ? {
              isAddonAfter
            }
          : null;

      const mergedProps = {
        disabled: addOn.props.disabled || disabled,
        ...iconProps,
        ...addonProps,
        className: clsx(
          addOn.props.className,
          isAddonAfter ? styles.addonAfter : styles.addonBefore
        ),
        style: addOn.props.style
      };

      return cloneElement(addOn, mergedProps);
    };

    const helperTextId = `${id || ariaId}-helper`;
    const errorWarningTextId = `${id || ariaId}-error`;

    let addonBefore = addonBeforeProp;
    let addonAfter = addonAfterProp;

    // ensure textfield has a label
    checkForA11yProps('TextField', props, [
      'label',
      'aria-label',
      'aria-labelledby'
    ]);

    if (icon) {
      logger.warn(
        'Icon prop is deprecated. Please use addonBefore or addonAfter instead.'
      );
      if (iconPosition === 'left') {
        addonBefore = icon;
      } else {
        addonAfter = icon;
      }
    }

    if (assignInputRef) {
      logger.warn('assignInputRef prop is deprecated. Please use ref instead.');
    }

    if (html.placeholder && ghostText) {
      logger.error('Only provide either placeholder or ghostText.');
    }

    const errorWarningText = errorText || warningText;
    const ariaValidationAttributes = getAriaAttributes(
      errorWarningText,
      helperText,
      errorWarningTextId,
      helperTextId
    );

    const { themeClass } = useTheme({ theme, colorScheme }, styles);

    const ghostTextElement = ghostText && (
      <B2 className={styles.TFGhostTextWrapper}>{ghostText}</B2>
    );

    const handleFocus = useCallback(
      (e: FocusEvent) => {
        setHasFocus(true);

        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (e: FocusEvent) => {
        setHasFocus(false);

        if (onBlur) {
          onBlur(e);
        }
      },
      [onBlur]
    );

    return (
      <div
        className={clsx(componentClassName, className, styles.TextFieldWrapper)}
        style={{ ...style, width: width || 'fit-content' }}
      >
        <label
          ref={getLabelRef}
          htmlFor={id || ariaId}
          className={clsx(
            styles.TFLabelWrapper,
            disabled && styles.TFLabelDisabled,
            label && styles.TFHasLabel
          )}
        >
          <B3>{hasWeightedLabel ? <Demi>{label}</Demi> : label}</B3>
          <div
            className={clsx(
              styles.TFInputWrapper,
              disabled && styles.TFInputDisabled,
              !errorWarningText && styles.TFNoErrorText,
              disabled && styles.TFDisabled,
              !disabled && styles.TFNotDisabled,
              (errorText || validationError) && styles.TFErrorText,
              warningText && styles.TFWarningText,
              hasFocus && styles.hasFocus,
              readOnly && !disableReadOnlyStyle && styles.TFReadOnly,
              themeClass
            )}
          >
            {ghostTextElement}
            {addonBefore && renderAddOn(addonBefore)}
            <input
              ref={ref || getInputRef}
              data-automation-id={automationId}
              id={id || ariaId}
              disabled={disabled}
              aria-invalid={!!errorText}
              width={width}
              className={clsx(
                IDS_PREFIX_FORM_CLASS,
                styles.TFInput,
                addonAfter && styles.hasAddonAfter,
                addonBefore && styles.hasAddonBefore,
                hideArrows && styles.TFInputHideArrows
              )}
              type={type}
              readOnly={readOnly}
              onFocus={handleFocus}
              onBlur={handleBlur}
              {...html}
              {...ariaValidationAttributes}
            />
            {addonAfter && renderAddOn(addonAfter, true)}
          </div>
        </label>
        <div className={clsx(footerTextClass)}>
          {errorWarningText && (
            <InlineValidationMessage
              id={errorWarningTextId}
              message={errorWarningText}
              // eslint-disable-next-line no-nested-ternary
              type={errorText ? 'error' : warningText ? 'warning' : 'invalid'}
            />
          )}
          {helperText && (
            <div
              id={helperTextId}
              className={clsx(
                styles.TFHelperTextWrapper,
                disabled && styles.TFHelperDisabled
              )}
            >
              <B3>{helperText}</B3>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default TextField;
